/**
 * Residential Permit Discovery — ADU, renovation, solar, deck
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Stack,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowBack as BackIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import FeeDisplay from '../../components/permit/FeeDisplay';

const PROJECT_TYPES = [
  'ADU (Accessory Dwelling Unit)',
  'Renovation / Remodel',
  'Solar panels',
  'Deck / Patio',
  'Fence',
  'Other residential',
];

const SCOPES = {
  small: 'Under $10,000',
  medium: '$10,000 – $50,000',
  large: '$50,000 – $500,000',
  xlarge: 'Over $500,000',
};

const getPermitSuggestions = (projectType, location, scope) => {
  const permits = [];
  if (projectType?.toLowerCase().includes('adu')) {
    permits.push({ name: 'Building permit', desc: 'Required for new dwelling units', typical: '4–8 weeks' });
    permits.push({ name: 'Zoning review', desc: 'ADU zoning compliance', typical: '2–4 weeks' });
  }
  if (projectType?.toLowerCase().includes('renovation') || projectType?.toLowerCase().includes('remodel')) {
    permits.push({ name: 'Building permit', desc: 'Required for structural changes', typical: '2–4 weeks' });
  }
  if (projectType?.toLowerCase().includes('solar')) {
    permits.push({ name: 'Solar permit', desc: 'Rooftop solar installation', typical: '1–3 weeks' });
    permits.push({ name: 'Electrical permit', desc: 'Grid connection and wiring', typical: '1–2 weeks' });
  }
  if (projectType?.toLowerCase().includes('deck') || projectType?.toLowerCase().includes('patio')) {
    permits.push({ name: 'Building permit', desc: 'Deck structure', typical: '2–4 weeks' });
  }
  if (projectType?.toLowerCase().includes('fence')) {
    permits.push({ name: 'Fence permit', desc: 'Fence height and location', typical: '1–3 weeks' });
  }
  if (permits.length === 0) {
    permits.push({ name: 'General permit inquiry', desc: 'Contact your local permitting office', typical: 'Varies' });
  }
  return permits;
};

export default function ResidentialPermitDemo({ onBack }) {
  const [step, setStep] = useState(0);
  const [projectType, setProjectType] = useState('');
  const [location, setLocation] = useState('');
  const [scope, setScope] = useState('');

  const permits = getPermitSuggestions(projectType, location, scope);
  const steps = ['Project type', 'Location', 'Scope'];
  const canProceed = step === 0 ? projectType : step === 1 ? location : scope;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onBack && (
          <Button size="small" startIcon={<BackIcon />} onClick={onBack}>Back</Button>
        )}
        <Typography variant="h6" sx={{ flex: 1 }}>Residential Permits</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        ADU, renovation, solar, deck — we&apos;ll suggest which permits you may need.
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {step === 0 && (
        <FormControl fullWidth>
          <InputLabel>What type of project?</InputLabel>
          <Select value={projectType} label="What type of project?" onChange={(e) => setProjectType(e.target.value)}>
            {PROJECT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {step === 1 && (
        <TextField fullWidth label="Address or city" value={location} onChange={(e) => setLocation(e.target.value)} />
      )}
      {step === 2 && (
        <FormControl fullWidth>
          <InputLabel>Estimated project cost</InputLabel>
          <Select value={scope} label="Estimated project cost" onChange={(e) => setScope(e.target.value)}>
            {Object.entries(SCOPES).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {step < 2 ? (
        <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!canProceed}>Next</Button>
      ) : (
        <Box>
          <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
            Based on your inputs, here are the permits you may need.
          </Alert>
          <Stack spacing={1}>
            {permits.map((p, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>{p.name}</Typography>
                <Typography variant="body2" color="text.secondary">{p.desc}</Typography>
                <Chip label={`Typical: ${p.typical}`} size="small" sx={{ mt: 1 }} />
              </Paper>
            ))}
          </Stack>
          <FeeDisplay
            permitType={permits[0]?.name?.toLowerCase().replace(/\s+/g, '-')}
            scope={scope}
            sqft={scope === 'large' ? 2000 : scope === 'xlarge' ? 5000 : scope === 'medium' ? 500 : null}
          />
          <Button variant="outlined" sx={{ mt: 2 }} onClick={() => { setStep(0); setProjectType(''); setLocation(''); setScope(''); }}>
            Start over
          </Button>
        </Box>
      )}
    </Stack>
  );
}
